import {
  Box,
  Flex,
  HStack,
  Icon,
  Image,
  PinInput,
  PinInputField,
  Text,
  Link as ChakraLink,
  useBreakpointValue,
} from '@chakra-ui/react';
import { useForm, Controller } from 'react-hook-form';
import CustomButton from '../../../components/common/Button';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import useCustomToast from '../../../components/common/Toast';
import { useResendEmailMutation, useVerifyEmailMutation } from '../../../services/auth/auth';
import { IVerifyEmailData } from '../../../services/auth/types';
import { NAVIGATION_ROUTES } from '../../../routes/constants';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import OutlineButton from '../../../components/common/UtilityButton';
import { AxiosError } from 'axios';
import { lazy, Suspense, useEffect, useState, useRef } from 'react';
import SliderSkeleton from '@/components/common/Slider/SliderSkeleton';
const Slider = lazy(() => import('@/components/common/Slider'));

import arrowLeft from '../../../assets/svg/backIcon.svg?react';
import Logo from '@/assets/svg/logo.svg';
import RedMail from '@/assets/svg/red-mail.svg?react';

// Validation schema
const otpSchema = yup.object().shape({
  otp: yup
    .string()
    .required('OTP is required')
    .length(6, 'OTP must be exactly 6 digits')
    .matches(/^\d{6}$/, 'OTP must be numeric'),
});

const EmailVerification = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { showToast } = useCustomToast();
  const { mutateAsync: verifyEmail } = useVerifyEmailMutation();
  const { mutateAsync: resendEmail } = useResendEmailMutation();
  const showSlider = useBreakpointValue({ base: false, lg: true });

  const hasAutoResent = useRef(false);

  // useForm must be called early
  const {
    control,
    handleSubmit,
    setError,
    clearErrors,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<IVerifyEmailData>({
    resolver: yupResolver(otpSchema),
  });

  const [modifiedData, setModifiedData] = useState(location.state || {});
  const emailParam = modifiedData?.email;

  useEffect(() => {
    if (location.state) {
      setModifiedData(location.state);
    }
  }, [location.state]);

  // Timer logic - exactly matching the reference implementation
  const initialTimer = () => {
    const storedTime = localStorage.getItem('resendOTPTimer');
    const currentTime = Date.now();
    if (storedTime) {
      const remainingTime = Math.ceil((Number(storedTime) - currentTime) / 1000);
      return Math.max(0, remainingTime);
    }
    return 120; // 2 minutes
  };

  const [timer, setTimer] = useState(initialTimer);
  const [isResendDisabled, setIsResendDisabled] = useState(timer > 0);

  // Set timer in localStorage only once when timer starts at 120
  useEffect(() => {
    if (timer === 120) {
      localStorage.setItem('resendOTPTimer', (Date.now() + 120 * 1000).toString());
    }
  }, []); // Run only on initial mount

  // Countdown logic
  useEffect(() => {
    let countdown: ReturnType<typeof setInterval>;

    if (timer > 0) {
      countdown = setInterval(() => {
        setTimer((prev) => {
          const updatedTime = prev - 1;
          if (updatedTime > 0) {
            localStorage.setItem('resendOTPTimer', (Date.now() + updatedTime * 1000).toString());
          }
          return updatedTime;
        });
      }, 1000);
    } else {
      setIsResendDisabled(false);
      localStorage.removeItem('resendOTPTimer');
    }

    return () => clearInterval(countdown);
  }, [timer]);

  // Auto-resend when redirected from SignIn (OTP not verified)
  useEffect(() => {
    const cameFromSignInOtpIssue =
      location.state?.fromSignInOtpNotVerified === true && emailParam && !hasAutoResent.current;

    if (cameFromSignInOtpIssue) {
      hasAutoResent.current = true;

      // Restart 2-minute timer
      setTimer(120);
      setIsResendDisabled(true);
      localStorage.setItem('resendOTPTimer', (Date.now() + 120 * 1000).toString());

      setValue('otp', '');
      clearErrors('otp');

      resendEmail({ email: emailParam })
        .then((response) => {
          showToast('success', response?.msg || 'Verification email resent');
        })
        .catch(() => {
          showToast('error', 'Failed to resend email. Please try again.');
        });
    }
  }, [emailParam, location.state, resendEmail, showToast, setValue, clearErrors]);

  const handleResend = async () => {
    setIsResendDisabled(true);
    setTimer(120);
    localStorage.setItem('resendOTPTimer', (Date.now() + 120 * 1000).toString());

    setValue('otp', '');
    clearErrors('otp');

    try {
      const response = await resendEmail({ email: emailParam });
      showToast('success', response?.msg || 'Email resent');
    } catch {
      showToast('error', 'An error occurred. Please try again.');
    }
  };

  const formatTimer = () => {
    const minutes = Math.floor(timer / 60);
    const seconds = timer % 60;
    return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
  };

  const onSubmit = async (data: IVerifyEmailData) => {
    try {
      const newData = { email: emailParam, otp: data.otp };
      const response = await verifyEmail(newData);
      showToast('success', response?.msg || 'Email verified successfully');
      navigate(NAVIGATION_ROUTES.LOGIN, { state: { email: emailParam } });
    } catch (error: unknown) {
      if (error instanceof AxiosError) {
        if (error.response?.data?.error) {
          setError('otp', {
            type: 'manual',
            message: error.response.data.error,
          });
        } else if (!error.response) {
          showToast('error', 'Network error. Please check your connection.');
        }
      } else {
        showToast('error', 'Something went wrong. Please try again.');
      }
    }
  };

  return (
    <Box display="flex" height="100vh" width="100vw">
      <Flex
        flex="1"
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        p={{ base: 0, lg: 4 }}
        position="relative"
        backgroundImage="url('/src/assets/png/background-images/background-pattern.png')"
        backgroundSize={{ base: '85vw', lg: '30vw' }}
        backgroundPosition={'center top'}
        backgroundRepeat="no-repeat"
      >
        <Box
          position="absolute"
          top={{ base: '16px', lg: '24px' }}
          left={{ base: '16px', lg: '24px' }}
          p={'sm'}
        >
          <Link to="/">
            <Image
              src={Logo}
              alt="Logo"
              width={{ base: '115px', lg: '170px' }}
              height={'auto'}
              objectFit={'cover'}
            />
          </Link>
        </Box>

        <Flex
          id="min-height"
          direction={'column'}
          justify={'start'}
          p={{ base: '16px', lg: 6 }}
          alignSelf="center"
          minWidth={{ base: '100%', lg: '500px' }}
          minHeight={{ base: '500px', sm: '500px' }}
          gap={'32px'}
        >
          <Icon
            as={RedMail}
            boxSize={12}
            p={3}
            borderRadius={'md'}
            color={'brand.primary.DEFAULT'}
            border={'1px solid'}
            borderColor={'border.default'}
          />
          <Flex direction={'column'} gap={'4px'}>
            <Text
              color={'text.primary'}
              fontSize={{ base: '2xl', lg: '4xl' }}
              fontFamily={'primary'}
              fontWeight={'semibold'}
            >
              Check your email
            </Text>
            <Box display={{ base: 'block', lg: 'flex' }} gap={'1'}>
              <Text
                color={'text.secondary'}
                fontSize={'sm'}
                fontFamily={'primary'}
                fontWeight={'normal'}
              >
                We sent a verification link to
              </Text>
              <Text
                color={'text.primary'}
                fontSize={'sm'}
                fontFamily={'primary'}
                fontWeight={'normal'}
              >
                {emailParam}
              </Text>
            </Box>
          </Flex>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Flex direction={'column'} gap={'24px'}>
              <Flex direction={'row'} gap="20px" pb="0" justifyContent={'space-between'}>
                <Flex direction={'column'} width={'100%'} gap={'16px'}>
                  <HStack justify="space-between">
                    <Controller
                      control={control}
                      name="otp"
                      render={({ field }) => (
                        <PinInput {...field}>
                          {[
                            'firstNumber',
                            'secondNumber',
                            'thirdNumber',
                            'fourthNumber',
                            'fifthNumber',
                            'sixthNumber',
                          ].map((name, idx) => (
                            <PinInputField
                              key={idx}
                              p={0}
                              m={0}
                              fontSize="20px"
                              textAlign="center"
                              color={'text.secondary'}
                              id={name}
                              fontWeight={'medium'}
                              borderRadius={'lg'}
                              borderWidth={'1px'}
                              borderColor={errors.otp ? 'status.error' : 'border.default'}
                              _focus={{
                                border: '2px',
                                borderColor: 'brand.primary.DEFAULT',
                                boxShadow: 'none',
                              }}
                              placeholder="0"
                              _placeholder={{
                                color: 'border.default',
                                fontSize: '20px',
                                textAlign: 'center',
                              }}
                              boxShadow={'xs'}
                              blur={'2px'}
                              width={'40px'}
                              height={'40px'}
                              onChange={(e) => field.onChange(e.target.value)}
                            />
                          ))}
                        </PinInput>
                      )}
                    />
                  </HStack>
                  {errors.otp && (
                    <Text fontFamily={'primary'} fontSize={'xs'} color={'status.error'}>
                      {errors.otp.message}
                    </Text>
                  )}
                </Flex>
              </Flex>

              <Flex direction={'column'} gap={'4'}>
                <CustomButton
                  borderWidth={'1'}
                  color={'text.white'}
                  border="1px"
                  borderColor="border.default"
                  borderRadius="lg"
                  fontFamily={'primary'}
                  fontSize={'16px'}
                  isLoading={isSubmitting}
                  type="submit"
                >
                  Verify Email
                </CustomButton>
              </Flex>
            </Flex>
          </form>

          {/* Resend section - exactly like the reference code */}
          <Flex id="didnt-section" justify={'center'} gap={'1'} fontFamily={'primary'}>
            <Text fontSize="sm" color="text.secondary" textAlign="center" fontFamily={'primary'}>
              Didn’t receive the email?{' '}
              {isResendDisabled ? (
                <Text as="span" color="gray.400" fontWeight="semibold">
                  Resend in {formatTimer()}
                </Text>
              ) : (
                <Text
                  as="span"
                  color="primary"
                  fontWeight="semibold"
                  cursor="pointer"
                  onClick={handleResend}
                >
                  Resend
                </Text>
              )}
            </Text>
          </Flex>

          {/* back to sign up */}
          <OutlineButton
            py={0}
            height={0}
            alignSelf={'center'}
            variant="noStyle"
            width="fit-content"
            fontSize="14px"
            fontWeight="semibold"
            fontFamily={'primary'}
            border={'none !important'}
            outline={'none'}
            icon={arrowLeft}
            text="Back to Sign Up"
            color={'brand.primary.DEFAULT'}
            onClick={() => navigate(NAVIGATION_ROUTES.SIGNUP)}
            _hover={{ color: 'brand.primary.700', textDecoration: 'underline' }}
          />
        </Flex>

        <Box position="absolute" bottom={'24px'} textAlign="center" width="100%">
          <Flex
            fontSize="xs"
            color="text.secondary"
            display={'flex'}
            justifyContent={'center'}
            gap={1}
            fontFamily={'primary'}
          >
            By creating an account, you agree to our{' '}
            <Text fontFamily={'primary'} textDecoration={'underline'} color="text.secondary">
              {' '}
              <ChakraLink href="https://kurtasari.com/terms-and-conditions" isExternal>
                terms of use
              </ChakraLink>
              .
            </Text>
          </Flex>
        </Box>
      </Flex>

      <Box flex="1" display={{ base: 'none', lg: 'block' }} bg="white" p={'24px'} overflow="hidden">
        {showSlider && (
          <Suspense fallback={<SliderSkeleton />}>
            <Slider />
          </Suspense>
        )}
      </Box>
    </Box>
  );
};

export default EmailVerification;
